import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRightLeft, BadgeCheck, Briefcase, Building2, CalendarClock, CalendarDays, CalendarPlus,
  CircleSlash, ClipboardList, Filter, GitBranch, History, Loader2, ListPlus, Route, Send,
  ShieldCheck, ThumbsDown, UserRound, UserCheck, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

const todayIso = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLES = {
  Scheduled: "bg-slate-100 text-slate-700 border-slate-200",
  Confirmed: "bg-sky-50 text-sky-700 border-sky-200",
  Attended: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "No Show": "bg-amber-50 text-amber-700 border-amber-200",
  Rescheduled: "bg-slate-100 text-slate-500 border-slate-200",
  "Second Round": "bg-violet-50 text-violet-700 border-violet-200",
  Hold: "bg-amber-50 text-amber-700 border-amber-200",
  "Awaiting Result": "bg-sky-50 text-sky-700 border-sky-200",
  "Selected (Pending Verification)": "bg-teal-50 text-teal-700 border-teal-200",
  Selected: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Joined: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Cancelled: "bg-red-50 text-red-600 border-red-200",
};

const EMPTY_FILTERS = { status: "all", company: "all", recruiter_id: "all", job_id: "all", date_from: "", date_to: "" };
const EMPTY_ROUND = { date: todayIso(), time: "", mode: "", interviewer: "", address: "", contact_person: "", remarks: "" };
const EMPTY_SELECTION = { selection_date: todayIso(), expected_joining_date: "", offered_salary: "", process: "", location: "", company_recruiter: "", remarks: "" };

export default function Interviews() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("open");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ modes: [], noshow_reasons: [], round_statuses: [] });
  const [recruiters, setRecruiters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);

  // schedule dialog
  const [schedOpen, setSchedOpen] = useState(false);
  const [lineups, setLineups] = useState([]);
  const [lineupsLoading, setLineupsLoading] = useState(false);
  const [schedLineup, setSchedLineup] = useState(null);
  const [schedApps, setSchedApps] = useState([]);
  const [round, setRound] = useState(EMPTY_ROUND);

  // per-record dialogs
  const [statusOf, setStatusOf] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", noshow_reason: "", feedback: "", remarks: "" });
  const [roundOf, setRoundOf] = useState(null);
  const [roundForm, setRoundForm] = useState({ ...EMPTY_ROUND, is_reschedule: false, reason: "" });
  const [selectOf, setSelectOf] = useState(null);
  const [selectionForm, setSelectionForm] = useState(EMPTY_SELECTION);
  const [verifyOf, setVerifyOf] = useState(null);
  const [joinOf, setJoinOf] = useState(null);
  const [joinPrep, setJoinPrep] = useState(null);
  const [joinForm, setJoinForm] = useState({ date_of_joining: "", company_id: "", locking_template: "", remarks: "" });
  const [rejectOf, setRejectOf] = useState(null);
  const [cancelOf, setCancelOf] = useState(null);
  const [reason, setReason] = useState("");
  const [detailOf, setDetailOf] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [timelineOf, setTimelineOf] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    api.get("/interviews/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/jobs", { params: { status: "all", limit: 100 } }).then((r) => setJobs(r.data || [])).catch(() => {});
    if (isAdmin) api.get("/users/recruiters").then((r) => setRecruiters(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(() => {
    setError(false);
    const params = {};
    if (tab === "open") params.outcome = "open";
    else if (tab !== "all") params.outcome = tab;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.company !== "all") params.company = filters.company;
    if (isAdmin && filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    if (filters.job_id !== "all") params.job_id = filters.job_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const calls = [api.get("/interviews", { params }), api.get("/interviews/kpis")];
    if (isAdmin) calls.push(api.get("/interviews/reports"));
    Promise.all(calls)
      .then(([l, k, r]) => {
        setItems(l.data);
        setKpis(k.data);
        if (r) setReports(r.data);
        const names = [...new Set(l.data.map((x) => x.company_display).filter(Boolean))];
        setCompanyOptions((prev) => [...new Set([...prev, ...names])]);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tab, filters, isAdmin]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // ---------------------------------------------------------------- schedule
  const openSchedule = () => {
    setSchedLineup(null);
    setSchedApps([]);
    setRound({ ...EMPTY_ROUND, mode: options.modes[0] || "" });
    setSchedOpen(true);
    setLineupsLoading(true);
    api.get("/interviews/eligible-lineups")
      .then((r) => setLineups(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLineupsLoading(false));
  };

  const submitSchedule = async () => {
    if (!schedLineup) return toast.error("Choose a Sent lineup");
    if (schedApps.length === 0) return toast.error("Select at least one candidate");
    if (!round.date) return toast.error("Interview date is required");
    if (!round.mode) return toast.error("Interview mode is required");
    setBusy(true);
    try {
      const res = await api.post("/interviews", { lineup_id: schedLineup.id, application_ids: schedApps, round });
      toast.success(`${res.data.length} interview(s) scheduled — ${res.data.map((i) => i.interview_code).join(", ")}`);
      setSchedOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- actions
  const doAction = async (fn, done) => {
    setBusy(true);
    try {
      await fn();
      done && done();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitStatus = () => doAction(async () => {
    if (!statusForm.status && !statusForm.feedback && !statusForm.remarks) throw { response: { data: { detail: "Nothing to update" } } };
    await api.put(`/interviews/${statusOf.id}/round-status`, {
      status: statusForm.status || statusOf.status,
      noshow_reason: statusForm.noshow_reason || null,
      feedback: statusForm.feedback || null,
      remarks: statusForm.remarks || null,
    });
    toast.success("Round updated");
  }, () => setStatusOf(null));

  const submitRound = () => doAction(async () => {
    if (!roundForm.date) throw { response: { data: { detail: "Date is required" } } };
    if (roundForm.is_reschedule && roundForm.reason.trim().length < 3) throw { response: { data: { detail: "A reschedule reason is mandatory" } } };
    await api.post(`/interviews/${roundOf.id}/rounds`, { ...roundForm, reason: roundForm.reason || null });
    toast.success(roundForm.is_reschedule ? "Interview rescheduled — previous round preserved in history" : "Next round scheduled");
  }, () => setRoundOf(null));

  const submitSelection = () => doAction(async () => {
    await api.post(`/interviews/${selectOf.id}/selection`, selectionForm);
    toast.success("Marked Selected (Pending Verification) — admin will verify");
  }, () => setSelectOf(null));

  const submitVerify = () => doAction(async () => {
    if (!selectionForm.selection_date || !selectionForm.expected_joining_date)
      throw { response: { data: { detail: "Selection date and expected joining date are required" } } };
    await api.post(`/interviews/${verifyOf.id}/verify-selection`, selectionForm);
    toast.success("Selection verified — outcome: Selected");
  }, () => setVerifyOf(null));

  const openJoined = (iv) => {
    setJoinOf(iv);
    setJoinPrep(null);
    setJoinForm({
      date_of_joining: iv.selection?.expected_joining_date || todayIso(),
      company_id: iv.company_id || "",
      locking_template: "",
      remarks: "",
    });
    api.get("/employment/prepare", { params: { application_id: iv.application_id } })
      .then((r) => {
        setJoinPrep(r.data);
        setJoinForm((f) => ({
          ...f,
          company_id: f.company_id || r.data.company_matches?.[0]?.id || "",
          locking_template: r.data.locking_templates?.[0]?.label || "",
        }));
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const submitJoined = () => doAction(async () => {
    if (!joinForm.date_of_joining) throw { response: { data: { detail: "Joining date is required" } } };
    if (!joinForm.company_id) throw { response: { data: { detail: "Choose the company (Company Master)" } } };
    if (!joinForm.locking_template) throw { response: { data: { detail: "Choose a locking template" } } };
    const res = await api.post(`/interviews/${joinOf.id}/joined`, joinForm);
    toast.success(res.data.employment.created
      ? `Joined — employment ${res.data.employment.emp_code} created in M04`
      : `Joined — linked to existing employment ${res.data.employment.emp_code}`);
  }, () => setJoinOf(null));

  const submitReject = () => doAction(async () => {
    if (reason.trim().length < 3) throw { response: { data: { detail: "A rejection reason is mandatory" } } };
    await api.post(`/interviews/${rejectOf.id}/reject`, { reason: reason.trim() });
    toast.success("Interview marked Rejected — candidate stays available for future applications");
  }, () => { setRejectOf(null); setReason(""); });

  const submitCancel = () => doAction(async () => {
    if (reason.trim().length < 3) throw { response: { data: { detail: "A cancellation reason is mandatory" } } };
    await api.post(`/interviews/${cancelOf.id}/cancel`, { reason: reason.trim() });
    toast.success("Interview cancelled");
  }, () => { setCancelOf(null); setReason(""); });

  const openHistory = (iv) => {
    setHistoryOf(iv);
    setSubLoading(true);
    api.get(`/interviews/${iv.id}/history`)
      .then((r) => setHistory(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setSubLoading(false));
  };

  const openTimeline = (iv) => {
    setTimelineOf(iv);
    setTimeline(null);
    setSubLoading(true);
    api.get(`/interviews/${iv.id}/timeline`)
      .then((r) => setTimeline(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setSubLoading(false));
  };

  const isOpen = (iv) => !iv.outcome;
  const filtersActive = filters.status !== "all" || filters.company !== "all" || filters.recruiter_id !== "all" ||
    filters.job_id !== "all" || filters.date_from || filters.date_to;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Interview Management</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Schedule interviews from Sent lineups, track every round, and manage selection to joining.
          </p>
        </div>
        <Button onClick={openSchedule} className="rounded-lg shrink-0" data-testid="interview-create-button">
          <CalendarPlus className="h-4 w-4 mr-2" /> Schedule Interview
        </Button>
      </div>

      {/* Dashboard cards */}
      {kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Today's Interviews" value={kpis.today} icon={CalendarDays} tone="primary" testId="interview-kpi-today" />
          <KpiCard label="Tomorrow" value={kpis.tomorrow} icon={CalendarClock} tone="slate" testId="interview-kpi-tomorrow" />
          <KpiCard label="Pending Results" value={kpis.pending_results} icon={ClipboardList} tone="warning" testId="interview-kpi-pending" />
          <KpiCard label="Second Round Pending" value={kpis.second_round_pending} icon={GitBranch} tone="slate" testId="interview-kpi-second-round" />
          <KpiCard label="Selected" value={kpis.selected} icon={BadgeCheck} tone="success" testId="interview-kpi-selected" />
          <KpiCard label="Rejected" value={kpis.rejected} icon={ThumbsDown} tone="warning" testId="interview-kpi-rejected" />
          <KpiCard label="No Show" value={kpis.no_show} icon={CircleSlash} tone="warning" testId="interview-kpi-noshow" />
          <KpiCard label="Joining This Week" value={kpis.joining_this_week} icon={UserCheck} tone="primary" testId="interview-kpi-joining-week" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg flex-wrap h-auto">
          <TabsTrigger value="open" className="rounded-md" data-testid="interview-tab-open">Open</TabsTrigger>
          <TabsTrigger value="Selected" className="rounded-md" data-testid="interview-tab-selected">Selected</TabsTrigger>
          <TabsTrigger value="Rejected" className="rounded-md" data-testid="interview-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="Cancelled" className="rounded-md" data-testid="interview-tab-cancelled">Cancelled</TabsTrigger>
          {isAdmin && <TabsTrigger value="all" className="rounded-md" data-testid="interview-tab-all">All</TabsTrigger>}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="rounded-xl border-border shadow-none" data-testid="interview-filters">
        <CardContent className="p-4">
          <div className="flex flex-col xl:flex-row xl:items-end gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground shrink-0 xl:pb-2.5">
              <Filter className="h-4 w-4" /> Filters
            </div>
            <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-6" : "lg:grid-cols-5"} gap-3 flex-1`}>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="interview-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.keys(STATUS_STYLES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Company</Label>
                <Select value={filters.company} onValueChange={(v) => setFilters({ ...filters, company: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="interview-filter-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All companies</SelectItem>
                    {companyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Recruiter</Label>
                  <Select value={filters.recruiter_id} onValueChange={(v) => setFilters({ ...filters, recruiter_id: v })}>
                    <SelectTrigger className="bg-white h-9" data-testid="interview-filter-recruiter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All recruiters</SelectItem>
                      {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Job</Label>
                <Select value={filters.job_id} onValueChange={(v) => setFilters({ ...filters, job_id: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="interview-filter-job"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All jobs</SelectItem>
                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} · {j.hiring_brand}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From (interview date)</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="interview-filter-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" className="bg-white h-9" value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="interview-filter-to" />
              </div>
            </div>
            {filtersActive && (
              <Button variant="ghost" size="sm" className="rounded-lg shrink-0" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="interview-filter-reset">
                <X className="h-3.5 w-3.5 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="interviews-error">
          <p className="text-sm text-muted-foreground">Could not load interviews.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No interviews here"
          description="Schedule interviews by picking candidates from a Sent lineup." />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Interview ID</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Round</TableHead>
                  <TableHead>Date · Mode</TableHead>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((iv) => (
                  <TableRow key={iv.id} className="hover:bg-muted/60" data-testid="interview-row">
                    <TableCell className="font-mono text-xs">
                      <button className="hover:text-primary hover:underline" onClick={() => setDetailOf(iv)} data-testid={`interview-detail-${iv.id}`}>
                        {iv.interview_code}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{iv.candidate_name}</TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{iv.company_display || "—"}</span>
                    </TableCell>
                    <TableCell className="text-sm">{iv.job_title}</TableCell>
                    <TableCell className="text-sm">R{iv.current_round}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDate(iv.latest_round_date)}{iv.latest_round_time ? ` ${iv.latest_round_time}` : ""}
                      <span className="text-xs text-muted-foreground ml-1">· {iv.rounds?.[iv.rounds.length - 1]?.mode}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3 text-muted-foreground" />{iv.recruiter_name || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full whitespace-nowrap ${STATUS_STYLES[iv.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}
                        data-testid="interview-status-badge">{iv.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {isOpen(iv) && iv.status !== "Selected (Pending Verification)" && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white"
                            onClick={() => { setStatusOf(iv); setStatusForm({ status: "", noshow_reason: "", feedback: "", remarks: "" }); }}
                            data-testid={`interview-status-${iv.id}`} title="Update round status / feedback">
                            <ClipboardList className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isOpen(iv) && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white"
                            onClick={() => { setRoundOf(iv); setRoundForm({ ...EMPTY_ROUND, mode: options.modes[0] || "", is_reschedule: false, reason: "" }); }}
                            data-testid={`interview-round-${iv.id}`} title="Reschedule / next round">
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isOpen(iv) && iv.status !== "Selected (Pending Verification)" && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white text-emerald-700"
                            onClick={() => { setSelectOf(iv); setSelectionForm(EMPTY_SELECTION); }}
                            data-testid={`interview-select-${iv.id}`} title="Mark Selected (Pending Verification)">
                            <BadgeCheck className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isAdmin && isOpen(iv) && iv.status === "Selected (Pending Verification)" && (
                          <Button size="sm" className="h-8 rounded-lg"
                            onClick={() => {
                              setVerifyOf(iv);
                              setSelectionForm({ ...EMPTY_SELECTION, ...(iv.selection || {}), selection_date: iv.selection?.selection_date || todayIso() });
                            }} data-testid={`interview-verify-${iv.id}`}>
                            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Verify
                          </Button>
                        )}
                        {isAdmin && iv.outcome === "Selected" && !iv.joined && (
                          <Button size="sm" className="h-8 rounded-lg" onClick={() => openJoined(iv)} data-testid={`interview-joined-${iv.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1.5" /> Mark Joined
                          </Button>
                        )}
                        {isOpen(iv) && (
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-destructive hover:text-destructive"
                            onClick={() => { setRejectOf(iv); setReason(""); }} data-testid={`interview-reject-${iv.id}`} title="Reject">
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isAdmin && isOpen(iv) && (
                          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-destructive hover:text-destructive"
                            onClick={() => { setCancelOf(iv); setReason(""); }} data-testid={`interview-cancel-${iv.id}`} title="Cancel">
                            <CircleSlash className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openTimeline(iv)}
                          data-testid={`interview-timeline-${iv.id}`} title="Candidate timeline">
                          <Route className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => openHistory(iv)}
                          data-testid={`interview-history-${iv.id}`} title="History">
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Admin reports */}
      {isAdmin && reports && (
        <div className="space-y-4" data-testid="interview-reports">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-xl border-border shadow-none"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Interview Conversion (Selection %)</p>
              <p className="text-2xl font-semibold mt-1" data-testid="report-selection-pct">{reports.totals.selection_pct}%</p>
              <p className="text-xs text-muted-foreground mt-0.5">{reports.totals.selected} of {reports.totals.total} interviews</p>
            </CardContent></Card>
            <Card className="rounded-xl border-border shadow-none"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">No Show %</p>
              <p className="text-2xl font-semibold mt-1" data-testid="report-noshow-pct">{reports.totals.no_show_pct}%</p>
              <p className="text-xs text-muted-foreground mt-0.5">{reports.totals.no_show} no-shows</p>
            </CardContent></Card>
            <Card className="rounded-xl border-border shadow-none"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Pending Results</p>
              <p className="text-2xl font-semibold mt-1" data-testid="report-pending-results">{reports.totals.pending_results}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Attended / awaiting result</p>
            </CardContent></Card>
            <Card className="rounded-xl border-border shadow-none"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Reschedules</p>
              <p className="text-2xl font-semibold mt-1" data-testid="report-reschedules">{reports.totals.reschedules}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Across all interviews</p>
            </CardContent></Card>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Recruiter report</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {reports.by_recruiter.length === 0 && <p className="text-sm text-muted-foreground">No interviews yet.</p>}
                {reports.by_recruiter.map((r) => (
                  <div key={r.recruiter} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 gap-2" data-testid="report-recruiter-row">
                    <span className="text-sm truncate">{r.recruiter}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{r.total} · S {r.selected} · R {r.rejected} · {r.selection_pct}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Company report</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {reports.by_company.length === 0 && <p className="text-sm text-muted-foreground">No interviews yet.</p>}
                {reports.by_company.map((r) => (
                  <div key={r.company} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 gap-2" data-testid="report-company-row">
                    <span className="text-sm truncate">{r.company}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{r.total} · S {r.selected} · {r.selection_pct}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Round-wise & daily</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 max-h-64 overflow-y-auto">
                {reports.round_wise.map((r) => (
                  <div key={r.round} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid="report-round-row">
                    <span className="text-sm">Round {r.round}</span>
                    <span className="text-xs text-muted-foreground">{r.reached} reached · {r.selected} selected</span>
                  </div>
                ))}
                {reports.daily.slice(0, 7).map((d) => (
                  <div key={d.date} className="flex items-center justify-between rounded-lg border border-dashed border-border px-3 py-2" data-testid="report-daily-row">
                    <span className="text-sm">{formatDate(d.date)}</span>
                    <Badge variant="outline" className="rounded-full">{d.count}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Schedule dialog */}
      <Dialog open={schedOpen} onOpenChange={(v) => !v && setSchedOpen(false)}>
        <DialogContent className="sm:max-w-lg rounded-xl max-h-[90vh] overflow-y-auto" data-testid="interview-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Schedule interview</DialogTitle>
            <DialogDescription>Interviews are created only from Sent lineups — one interview per candidate per lineup.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Sent lineup</Label>
              {lineupsLoading ? <Skeleton className="h-10 w-full rounded" /> : (
                <Select value={schedLineup?.id || ""} onValueChange={(v) => { setSchedLineup(lineups.find((l) => l.id === v)); setSchedApps([]); }}>
                  <SelectTrigger className="bg-white" data-testid="schedule-lineup-select"><SelectValue placeholder="Choose Sent lineup" /></SelectTrigger>
                  <SelectContent>
                    {lineups.length === 0 && <p className="text-sm text-muted-foreground p-3">No Sent lineups with available candidates.</p>}
                    {lineups.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.lineup_code} · {l.company_display} · {l.job_title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {schedLineup && (
              <div className="space-y-1.5">
                <Label>Candidates <span className="text-muted-foreground">({schedApps.length} selected)</span></Label>
                <div className="rounded-lg border border-border max-h-44 overflow-y-auto divide-y divide-border" data-testid="schedule-candidate-picker">
                  {schedLineup.candidates.map((c) => (
                    <label key={c.application_id} className={`flex items-center gap-3 px-3 py-2 ${c.interview_code ? "opacity-60" : "cursor-pointer hover:bg-muted/60"}`}
                      data-testid="schedule-candidate-option">
                      <Checkbox disabled={!!c.interview_code} checked={schedApps.includes(c.application_id)}
                        onCheckedChange={() => setSchedApps((p) => p.includes(c.application_id) ? p.filter((x) => x !== c.application_id) : [...p, c.application_id])} />
                      <span className="text-sm flex-1 min-w-0">
                        <span className="font-medium">{c.candidate_name}</span>
                        <span className="text-xs text-muted-foreground ml-2 font-mono">{c.app_code}</span>
                      </span>
                      {c.interview_code && <Badge variant="outline" className="rounded-full text-[10px]">{c.interview_code}</Badge>}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" className="bg-white" value={round.date} onChange={(e) => setRound({ ...round, date: e.target.value })} data-testid="schedule-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Time <span className="text-muted-foreground">(optional)</span></Label>
                <Input type="time" className="bg-white" value={round.time} onChange={(e) => setRound({ ...round, time: e.target.value })} data-testid="schedule-time-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <Select value={round.mode} onValueChange={(v) => setRound({ ...round, mode: v })}>
                  <SelectTrigger className="bg-white" data-testid="schedule-mode-select"><SelectValue placeholder="Mode" /></SelectTrigger>
                  <SelectContent>{options.modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Interviewer <span className="text-muted-foreground">(optional)</span></Label>
                <Input className="bg-white" value={round.interviewer} onChange={(e) => setRound({ ...round, interviewer: e.target.value })} data-testid="schedule-interviewer-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Contact person <span className="text-muted-foreground">(optional)</span></Label>
                <Input className="bg-white" value={round.contact_person} onChange={(e) => setRound({ ...round, contact_person: e.target.value })} data-testid="schedule-contact-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Address <span className="text-muted-foreground">(optional)</span></Label>
                <Input className="bg-white" value={round.address} onChange={(e) => setRound({ ...round, address: e.target.value })} data-testid="schedule-address-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Remarks <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={round.remarks} onChange={(e) => setRound({ ...round, remarks: e.target.value })} data-testid="schedule-remarks-input" />
            </div>
            <Button onClick={submitSchedule} disabled={busy} className="w-full rounded-lg" data-testid="schedule-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Schedule Interview(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Round status dialog */}
      <Dialog open={!!statusOf} onOpenChange={(v) => !v && setStatusOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="interview-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update round — {statusOf?.interview_code}</DialogTitle>
            <DialogDescription>Round {statusOf?.current_round} · current status: {statusOf?.status}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={statusForm.status || statusOf?.status || ""} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                <SelectTrigger className="bg-white" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.round_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {statusForm.status === "No Show" && (
              <div className="space-y-1.5">
                <Label>No Show reason <span className="text-destructive">*</span></Label>
                <Select value={statusForm.noshow_reason} onValueChange={(v) => setStatusForm({ ...statusForm, noshow_reason: v })}>
                  <SelectTrigger className="bg-white" data-testid="noshow-reason-select"><SelectValue placeholder="Mandatory reason" /></SelectTrigger>
                  <SelectContent>
                    {options.noshow_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">You can immediately schedule another interview afterwards.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Feedback <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={statusForm.feedback}
                onChange={(e) => setStatusForm({ ...statusForm, feedback: e.target.value })} data-testid="status-feedback-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Remarks <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={statusForm.remarks}
                onChange={(e) => setStatusForm({ ...statusForm, remarks: e.target.value })} data-testid="status-remarks-input" />
            </div>
            <Button onClick={submitStatus} disabled={busy} className="w-full rounded-lg" data-testid="status-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update Round
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add round / reschedule dialog */}
      <Dialog open={!!roundOf} onOpenChange={(v) => !v && setRoundOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="interview-round-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{roundForm.is_reschedule ? "Reschedule" : "Next round"} — {roundOf?.interview_code}</DialogTitle>
            <DialogDescription>
              Rounds are append-only. {roundForm.is_reschedule
                ? `The current round stays in history as Rescheduled; a new attempt for round ${roundOf?.current_round} is added.`
                : `A new round ${roundOf ? roundOf.current_round + 1 : ""} will be appended.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <Label htmlFor="reschedule-switch" className="cursor-pointer">This is a reschedule (same round)</Label>
              <Switch id="reschedule-switch" checked={roundForm.is_reschedule}
                onCheckedChange={(v) => setRoundForm({ ...roundForm, is_reschedule: v })} data-testid="round-reschedule-switch" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" className="bg-white" value={roundForm.date} onChange={(e) => setRoundForm({ ...roundForm, date: e.target.value })} data-testid="round-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Time <span className="text-muted-foreground">(optional)</span></Label>
                <Input type="time" className="bg-white" value={roundForm.time} onChange={(e) => setRoundForm({ ...roundForm, time: e.target.value })} data-testid="round-time-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Mode</Label>
                <Select value={roundForm.mode} onValueChange={(v) => setRoundForm({ ...roundForm, mode: v })}>
                  <SelectTrigger className="bg-white" data-testid="round-mode-select"><SelectValue placeholder="Mode" /></SelectTrigger>
                  <SelectContent>{options.modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Interviewer <span className="text-muted-foreground">(optional)</span></Label>
                <Input className="bg-white" value={roundForm.interviewer} onChange={(e) => setRoundForm({ ...roundForm, interviewer: e.target.value })} data-testid="round-interviewer-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reason {roundForm.is_reschedule ? <span className="text-destructive">*</span> : <span className="text-muted-foreground">(optional)</span>}</Label>
              <Textarea rows={2} className="bg-white" value={roundForm.reason}
                placeholder={roundForm.is_reschedule ? "Why is this interview being rescheduled? (mandatory, audited)" : "Notes for the new round"}
                onChange={(e) => setRoundForm({ ...roundForm, reason: e.target.value })} data-testid="round-reason-input" />
            </div>
            <Button onClick={submitRound} disabled={busy} className="w-full rounded-lg" data-testid="round-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {roundForm.is_reschedule ? "Reschedule Interview" : "Add Next Round"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Selection (pending) dialog */}
      <Dialog open={!!selectOf} onOpenChange={(v) => !v && setSelectOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="interview-selection-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark Selected — {selectOf?.interview_code}</DialogTitle>
            <DialogDescription>
              Sets status to <b>Selected (Pending Verification)</b>. Admin verifies before it becomes final. No employment is created here.
            </DialogDescription>
          </DialogHeader>
          <SelectionFields form={selectionForm} setForm={setSelectionForm} />
          <Button onClick={submitSelection} disabled={busy} className="w-full rounded-lg" data-testid="selection-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Mark Selected (Pending Verification)
          </Button>
        </DialogContent>
      </Dialog>

      {/* Verify selection dialog (admin) */}
      <Dialog open={!!verifyOf} onOpenChange={(v) => !v && setVerifyOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="interview-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Verify selection — {verifyOf?.interview_code}</DialogTitle>
            <DialogDescription>
              Confirm with the company/candidate and record final details. Outcome becomes <b>Selected</b>; employment is created only at Mark Joined.
            </DialogDescription>
          </DialogHeader>
          <SelectionFields form={selectionForm} setForm={setSelectionForm} required />
          <Button onClick={submitVerify} disabled={busy} className="w-full rounded-lg" data-testid="verify-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify Selection
          </Button>
        </DialogContent>
      </Dialog>

      {/* Mark Joined dialog (admin) */}
      <Dialog open={!!joinOf} onOpenChange={(v) => !v && setJoinOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="interview-joined-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark as Joined — {joinOf?.interview_code}</DialogTitle>
            <DialogDescription>
              Confirms actual joining and auto-creates the Employment record in M04 (idempotent — one per application, never duplicated).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {joinPrep?.existing_employment && (
              <p className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-2.5" data-testid="joined-existing-warning">
                An employment already exists for this application ({joinPrep.existing_employment.emp_code}) — it will be linked, not duplicated.
              </p>
            )}
            {joinPrep?.application?.stage && joinPrep.application.stage !== "Selected" && !joinPrep?.existing_employment && (
              <p className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-2.5" data-testid="joined-stage-warning">
                M04 rule: the application must be in stage "Selected" (currently "{joinPrep.application.stage}"). Update the application pipeline first.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date of joining</Label>
                <Input type="date" className="bg-white" value={joinForm.date_of_joining}
                  onChange={(e) => setJoinForm({ ...joinForm, date_of_joining: e.target.value })} data-testid="joined-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Company (Master)</Label>
                <Select value={joinForm.company_id} onValueChange={(v) => setJoinForm({ ...joinForm, company_id: v })}>
                  <SelectTrigger className="bg-white" data-testid="joined-company-select"><SelectValue placeholder="Choose company" /></SelectTrigger>
                  <SelectContent>
                    {(joinPrep?.companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Locking period template</Label>
              <Select value={joinForm.locking_template} onValueChange={(v) => setJoinForm({ ...joinForm, locking_template: v })}>
                <SelectTrigger className="bg-white" data-testid="joined-locking-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
                <SelectContent>
                  {(joinPrep?.locking_templates || []).map((t) => <SelectItem key={t.label} value={t.label}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Remarks <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={joinForm.remarks}
                onChange={(e) => setJoinForm({ ...joinForm, remarks: e.target.value })} data-testid="joined-remarks-input" />
            </div>
            <Button onClick={submitJoined} disabled={busy} className="w-full rounded-lg" data-testid="joined-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm Joined & Create Employment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectOf} onOpenChange={(v) => !v && setRejectOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="interview-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reject — {rejectOf?.interview_code}</DialogTitle>
            <DialogDescription>Final outcome. The candidate remains available for future applications and lineups.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Rejection reason <span className="text-destructive">*</span></Label>
              <Textarea rows={2} className="bg-white" value={reason} placeholder="Why was the candidate rejected? (mandatory, audited)"
                onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
            </div>
            <Button variant="destructive" onClick={submitReject} disabled={busy} className="w-full rounded-lg" data-testid="reject-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Mark Rejected
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog (admin) */}
      <Dialog open={!!cancelOf} onOpenChange={(v) => !v && setCancelOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="interview-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel — {cancelOf?.interview_code}</DialogTitle>
            <DialogDescription>Cancellation is terminal and requires a reason. History is preserved forever.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cancellation reason <span className="text-destructive">*</span></Label>
              <Textarea rows={2} className="bg-white" value={reason} placeholder="Why is this interview being cancelled? (mandatory)"
                onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
            </div>
            <Button variant="destructive" onClick={submitCancel} disabled={busy} className="w-full rounded-lg" data-testid="cancel-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Cancel Interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog — rounds */}
      <Dialog open={!!detailOf} onOpenChange={(v) => !v && setDetailOf(null)}>
        <DialogContent className="sm:max-w-lg rounded-xl max-h-[85vh] overflow-y-auto" data-testid="interview-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detailOf?.interview_code} — {detailOf?.candidate_name}</DialogTitle>
            <DialogDescription>
              {detailOf?.company_display} · {detailOf?.job_title} · from lineup <span className="font-mono">{detailOf?.lineup_code}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(detailOf?.rounds || []).map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-3" data-testid="interview-detail-round">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-medium">
                    Round {r.round_number}{r.is_reschedule ? " (rescheduled attempt)" : ""}
                    <span className="text-xs text-muted-foreground ml-2">{formatDate(r.date)}{r.time ? ` ${r.time}` : ""} · {r.mode}</span>
                  </p>
                  <Badge variant="outline" className={`rounded-full ${STATUS_STYLES[r.status] || ""}`}>{r.status}</Badge>
                </div>
                {(r.interviewer || r.contact_person || r.address) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {[r.interviewer && `Interviewer: ${r.interviewer}`, r.contact_person && `Contact: ${r.contact_person}`, r.address && `Address: ${r.address}`].filter(Boolean).join(" · ")}
                  </p>
                )}
                {r.noshow_reason && <p className="text-xs text-amber-700 mt-1">No Show reason: {r.noshow_reason}</p>}
                {r.feedback && <p className="text-xs mt-1">Feedback: {r.feedback}</p>}
                {r.remarks && <p className="text-xs text-muted-foreground mt-1">{r.remarks}</p>}
              </div>
            ))}
            {detailOf?.selection && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-xs space-y-0.5" data-testid="interview-detail-selection">
                <p className="font-medium text-sm text-emerald-800">Selection {detailOf.selection.verified ? "(verified)" : "(pending verification)"}</p>
                {detailOf.selection.selection_date && <p>Selection date: {formatDate(detailOf.selection.selection_date)}</p>}
                {detailOf.selection.expected_joining_date && <p>Expected joining: {formatDate(detailOf.selection.expected_joining_date)}</p>}
                {detailOf.selection.offered_salary && <p>Offered salary: {detailOf.selection.offered_salary}</p>}
                {detailOf.selection.process && <p>Process: {detailOf.selection.process}</p>}
                {detailOf.selection.location && <p>Location: {detailOf.selection.location}</p>}
                {detailOf.selection.company_recruiter && <p>Company recruiter: {detailOf.selection.company_recruiter}</p>}
              </div>
            )}
            {detailOf?.joined && (
              <p className="text-xs rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 p-2.5" data-testid="interview-detail-joined">
                Joined {formatDate(detailOf.joined.date_of_joining)} — employment <span className="font-mono">{detailOf.joined.emp_code}</span>
              </p>
            )}
            {detailOf?.rejection && <p className="text-xs text-red-600">Rejected: {detailOf.rejection.reason}</p>}
            {detailOf?.cancellation_reason && <p className="text-xs text-red-600">Cancelled: {detailOf.cancellation_reason}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Timeline dialog */}
      <Dialog open={!!timelineOf} onOpenChange={(v) => !v && setTimelineOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="interview-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Candidate timeline — {timelineOf?.candidate_name}</DialogTitle>
            <DialogDescription>Complete derived journey: application → lineup → interview rounds → outcome → employment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-0">
            {subLoading && <Skeleton className="h-32 w-full rounded-lg" />}
            {!subLoading && timeline?.events?.map((e, idx) => (
              <div key={idx} className="flex gap-3" data-testid="timeline-event">
                <div className="flex flex-col items-center">
                  <span className={`h-2.5 w-2.5 rounded-full mt-1.5 shrink-0 ${e.kind === "outcome" ? "bg-emerald-500" : e.kind === "forwarded_lead" ? "bg-amber-400" : "bg-primary/60"}`} />
                  {idx < timeline.events.length - 1 && <span className="w-px flex-1 bg-border" />}
                </div>
                <div className="pb-4 min-w-0">
                  <p className="text-sm font-medium">{e.title}</p>
                  {e.subtitle && <p className="text-xs text-muted-foreground mt-0.5">{e.subtitle}</p>}
                  {e.feedback && <p className="text-xs text-muted-foreground mt-0.5">Feedback: {e.feedback}</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{formatDateTime(e.at)}{e.by ? ` · by ${e.by}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="interview-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">History — {historyOf?.interview_code}</DialogTitle>
            <DialogDescription>Immutable log of every change. Never edited or deleted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {subLoading && <Skeleton className="h-24 w-full rounded-lg" />}
            {!subLoading && history.map((e) => (
              <div key={e.id} className="rounded-lg border border-border p-3" data-testid="interview-history-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {e.event_type.replaceAll("_", " ")}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                </div>
                {e.previous_status && e.new_status && (
                  <p className="text-sm mt-1.5">{e.previous_status}<span className="text-muted-foreground mx-1.5">→</span><span className="font-medium">{e.new_status}</span></p>
                )}
                {e.reason && <p className="text-xs text-amber-700 mt-1">Reason: {e.reason}</p>}
                {e.details?.previous_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(e.details.previous_date)} <span className="mx-1">→</span>
                    <span className="text-foreground">{formatDate(e.details.new_date)}</span>
                  </p>
                )}
                {e.details?.employment && <p className="text-xs mt-1 font-mono">{e.details.employment}</p>}
                {e.remarks && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.remarks}</p>}
                <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span></p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SelectionFields = ({ form, setForm, required = false }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label>Selection date {required && <span className="text-destructive">*</span>}</Label>
        <Input type="date" className="bg-white" value={form.selection_date || ""}
          onChange={(e) => setForm({ ...form, selection_date: e.target.value })} data-testid="selection-date-input" />
      </div>
      <div className="space-y-1.5">
        <Label>Expected joining {required && <span className="text-destructive">*</span>}</Label>
        <Input type="date" className="bg-white" value={form.expected_joining_date || ""}
          onChange={(e) => setForm({ ...form, expected_joining_date: e.target.value })} data-testid="selection-joining-input" />
      </div>
      <div className="space-y-1.5">
        <Label>Offered salary</Label>
        <Input className="bg-white" value={form.offered_salary || ""} placeholder="e.g. 18,000/month"
          onChange={(e) => setForm({ ...form, offered_salary: e.target.value })} data-testid="selection-salary-input" />
      </div>
      <div className="space-y-1.5">
        <Label>Process</Label>
        <Input className="bg-white" value={form.process || ""} placeholder="e.g. Customer Support"
          onChange={(e) => setForm({ ...form, process: e.target.value })} data-testid="selection-process-input" />
      </div>
      <div className="space-y-1.5">
        <Label>Location</Label>
        <Input className="bg-white" value={form.location || ""}
          onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="selection-location-input" />
      </div>
      <div className="space-y-1.5">
        <Label>Company recruiter</Label>
        <Input className="bg-white" value={form.company_recruiter || ""}
          onChange={(e) => setForm({ ...form, company_recruiter: e.target.value })} data-testid="selection-company-recruiter-input" />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label>Remarks <span className="text-muted-foreground">(optional)</span></Label>
      <Textarea rows={2} className="bg-white" value={form.remarks || ""}
        onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="selection-remarks-input" />
    </div>
  </div>
);
